<script setup lang="ts">
/* Stores */
import { useUserStore } from '@/store/user/user'
import { useAppStore } from '@/store/app/app'

/* Types */
import {
  NotFoundError,
  ServerError,
  HttpStatusCode,
  Success,
} from '@/api/results'

/* Components */
import BonusSectionSave from '@/components/Bonus/sections/BonusSectionSave.vue'
import BonusSectionApply from '@/components/Bonus/sections/BonusSectionApply.vue'
import BonusSectionSpend from '@/components/Bonus/sections/BonusSectionSpend.vue'
import BonusSectionInfo from '@/components/Bonus/sections/BonusSectionInfo.vue'

const userStore = useUserStore()
const appStore = useAppStore()
const { isDesktopOrTablet, isMobile } = useDevice()

const isWebview = computed(() => appStore.isWebview)

const showBreadcrumb = computed(
  () => page?.value?.breadcrumbs?.length !== 0 && !isWebview.value,
)

const pageTitle = computed(() => {
  if (isMobile) return 'Бонусы'

  if (isDesktopOrTablet) return page?.value?.title

  return ''
})

const { data: page, error } = await useAsyncData(async (nuxtApp) => {
  const result = await nuxtApp?.$apiService.site.fetchBonusPage()

  if (result instanceof NotFoundError) {
    createError({
      statusCode: HttpStatusCode.NOT_FOUND,
    })
  }

  if (result instanceof ServerError) {
    throw createError({
      statusCode: HttpStatusCode.INTERNAL_SERVER_ERROR,
    })
  }

  if (result instanceof Success) {
    return result.data
  }
})

if (error.value) {
  showError(error.value)
}

onMounted(() => {
  if (userStore.isAuthenticated) userStore.getStatistic()
})
</script>

<template>
  <div class="bonus-page">
    <app-head
      :title="page?.seo?.seo_title"
      :seo="{
        title: page?.seo.seo_title,
        description: page?.seo.seo_description,
        keywords: page?.seo.seo_title,
      }"
    />

    <app-breadcrumbs
      v-if="showBreadcrumb"
      class="bonus-page__breadcrumbs"
      :server-breadcrumbs="page?.breadcrumbs"
    />

    <h1 v-if="pageTitle" class="bonus-page__title">
      {{ pageTitle }}
    </h1>

    <bonus-section-info
      v-if="page?.bonus_info_text"
      class="bonus-page__section"
      :page="page"
    />

    <bonus-section-save
      v-if="page?.howto_save_up_text"
      class="bonus-page__section"
      :page="page"
    />

    <bonus-section-spend
      v-if="page?.howto_spend_text"
      class="bonus-page__section"
      :page="page"
    />

    <bonus-section-apply
      v-if="page?.bonus_dont_apply_text"
      class="bonus-page__section"
      :page="page"
    />
  </div>
</template>

<style lang="scss" scoped>
.bonus-page {
  padding-top: 0;
  margin: 24px 0 64px;
  @include media('>=desktop-mini') {
    padding-top: 30px;
    margin: 10px 0 90px;
  }

  &__title {
    margin: 20px 0 24px;
    font-size: 18px;
    line-height: 21px;
    @include media('>=desktop-mini') {
      margin-bottom: 48px;
      font-size: 28px;
      font-weight: 800;
      line-height: 56px;
    }
  }

  &__section {
    margin-bottom: 24px;
    @include media('>=desktop-mini') {
      display: flex;
      justify-content: space-between;
      margin-bottom: 56px;
    }
  }
}
</style>
